import React from 'react';
import { ChatMessage as ChatMessageType, ChatMessageMetadata } from '../../types/chat';

/**
 * ChatMessage - Renders a single chat message bubble
 *
 * Requirements:
 * - 1.3: User messages and assistant responses displayed in thread
 * - 1.4: User messages right-aligned, assistant messages left-aligned
 * - WCAG 2.1 AA: proper role attributes and accessible structure
 */

interface ChatMessageProps {
  message: ChatMessageType;
}

/**
 * Format an ISO timestamp to a readable time string (e.g., "2:30 PM")
 */
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Lightweight markdown-to-JSX renderer for assistant messages.
 * Supports: **bold**, *italic*, `inline code`, ```code blocks```, - bullet lists, numbered lists
 */
function renderMarkdown(content: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const lines = content.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block: ```...```
    if (line.trim().startsWith('```')) {
      const codeLines: string[] = [];
      i++; // skip opening ```
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      nodes.push(
        <pre
          key={`code-${i}`}
          className="my-2 overflow-x-auto rounded bg-gray-900 p-3 text-sm text-muted-foreground/70"
        >
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Bullet list item: - item or * item
    if (/^\s*[-*]\s+/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      nodes.push(
        <ul key={`ul-${i}`} className="my-1 ml-4 list-disc space-y-0.5">
          {listItems.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list item: 1. item
    if (/^\s*\d+\.\s+/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      nodes.push(
        <ol key={`ol-${i}`} className="my-1 ml-4 list-decimal space-y-0.5">
          {listItems.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line → spacing
    if (line.trim() === '') {
      nodes.push(<div key={`br-${i}`} className="h-2" />);
      i++;
      continue;
    }

    // Regular paragraph
    nodes.push(
      <p key={`p-${i}`} className="my-0.5">
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return nodes;
}

/**
 * Render inline markdown: **bold**, *italic*, `code`
 */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex matches: **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+?)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Push text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      parts.push(
        <strong key={`b-${match.index}`} className="font-semibold">
          {match[2]}
        </strong>
      );
    } else if (match[3]) {
      // *italic*
      parts.push(
        <em key={`i-${match.index}`}>
          {match[3]}
        </em>
      );
    } else if (match[4]) {
      // `inline code`
      parts.push(
        <code
          key={`c-${match.index}`}
          className="rounded bg-secondary px-1 py-0.5 text-sm dark:bg-secondary"
        >
          {match[4]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Push remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Streaming indicator dots animation
 */
const StreamingIndicator: React.FC = () => (
  <span className="inline-flex items-center space-x-1" aria-label="Assistant is typing">
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/40 [animation-delay:-0.3s]" />
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/40 [animation-delay:-0.15s]" />
    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/40" />
  </span>
);

// ─── Confidence Indicator ─────────────────────────────────────────────────────

/**
 * Determine confidence level from score
 * Requirement 11.5: Confidence indicator based on Hallucination Detector score
 * - HIGH: ≥ 0.8 (green)
 * - MEDIUM: 0.6–0.8 (amber)
 * - LOW: < 0.6 (red)
 */
function getConfidenceLevel(score: number): { label: string; icon: string; className: string } {
  if (score >= 0.8) {
    return {
      label: 'HIGH',
      icon: '✅',
      className: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
    };
  }
  if (score >= 0.6) {
    return {
      label: 'MEDIUM',
      icon: '⚠️',
      className: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
    };
  }
  return {
    label: 'LOW',
    icon: '❌',
    className: 'bg-rose-500/10 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300',
  };
}

/**
 * Format a time range for display
 */
function formatTimeRange(timeRange: { start: string; end: string }): string {
  try {
    const start = new Date(timeRange.start).toLocaleDateString([], { month: 'short', day: 'numeric' });
    const end = new Date(timeRange.end).toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${start} – ${end}`;
  } catch {
    return `${timeRange.start} – ${timeRange.end}`;
  }
}

/**
 * ResponseMetadata - Displays confidence indicator, endpoints queried, and time range
 *
 * Requirements:
 * - 11.3: Metadata field showing which Cost_API endpoints were queried and time range
 * - 11.5: Confidence indicator based on Hallucination Detector validation score
 */
const ResponseMetadata: React.FC<{ metadata: ChatMessageMetadata }> = ({ metadata }) => {
  const { endpointsQueried, timeRange, confidenceScore } = metadata;

  // Only render if there's meaningful metadata to display
  const hasConfidence = confidenceScore !== undefined && confidenceScore !== null;
  const hasEndpoints = endpointsQueried && endpointsQueried.length > 0;
  const hasTimeRange = timeRange && timeRange.start && timeRange.end;

  if (!hasConfidence && !hasEndpoints && !hasTimeRange) {
    return null;
  }

  const confidence = hasConfidence ? getConfidenceLevel(confidenceScore) : null;

  return (
    <div
      className="mt-2 border-t border-border pt-2 dark:border-border"
      aria-label="Response metadata"
    >
      {/* Confidence indicator badge */}
      {confidence && (
        <div className="mb-1.5 flex items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${confidence.className}`}
            aria-label={`Confidence: ${confidence.label}`}
          >
            <span aria-hidden="true">{confidence.icon}</span>
            {confidence.label} confidence
          </span>
          <span className="text-xs text-muted-foreground/70">
            ({((confidenceScore ?? 0) * 100).toFixed(0)}%)
          </span>
        </div>
      )}

      {/* Endpoints queried */}
      {hasEndpoints && (
        <div className="mb-1 flex flex-wrap items-start gap-1 text-xs text-muted-foreground">
          <span className="font-medium">Endpoints:</span>
          {endpointsQueried.map((endpoint, idx) => (
            <code
              key={idx}
              className="rounded bg-secondary px-1 py-0.5 text-xs dark:bg-secondary"
            >
              {endpoint}
            </code>
          ))}
        </div>
      )}

      {/* Time range */}
      {hasTimeRange && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium">Data range:</span>{' '}
          {formatTimeRange(timeRange)}
        </div>
      )}
    </div>
  );
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { role, content, timestamp, isStreaming } = message;

  // User messages: right-aligned, blue background
  if (role === 'user') {
    return (
      <div
        className="flex justify-end"
        role="listitem"
        aria-label={`You said: ${content}`}
      >
        <div className="max-w-[80%] rounded-lg bg-primary px-4 py-2 text-white shadow-sm">
          <p className="text-sm whitespace-pre-wrap">{content}</p>
          <time
            className="mt-1 block text-right text-xs text-blue-200"
            dateTime={timestamp}
          >
            {formatTimestamp(timestamp)}
          </time>
        </div>
      </div>
    );
  }

  // Assistant messages: left-aligned, gray/white background with markdown
  if (role === 'assistant') {
    return (
      <div
        className="flex justify-start"
        role="listitem"
        aria-label={`Assistant said: ${content}`}
      >
        <div className="max-w-[80%] rounded-lg bg-secondary px-4 py-2 text-foreground shadow-sm dark:bg-secondary dark:text-foreground">
          <div className="text-sm">
            {content && renderMarkdown(content)}
            {isStreaming && !content && <StreamingIndicator />}
            {isStreaming && content && (
              <span className="ml-1 inline-block">
                <StreamingIndicator />
              </span>
            )}
          </div>
          {!isStreaming && message.metadata && (
            <ResponseMetadata metadata={message.metadata} />
          )}
          {!isStreaming && (
            <time
              className="mt-1 block text-xs text-muted-foreground"
              dateTime={timestamp}
            >
              {formatTimestamp(timestamp)}
            </time>
          )}
        </div>
      </div>
    );
  }

  // System messages: centered, subdued styling
  return (
    <div
      className="flex justify-center"
      role="listitem"
      aria-label={`System message: ${content}`}
    >
      <p className="max-w-[90%] text-center text-xs italic text-muted-foreground">
        {content}
      </p>
    </div>
  );
};
